//Reads a PLY file's ASCII header, extracts its elements, and verifies the file size a
//binary PLY implies: header bytes + sum(element count x record bytes) must equal the size
//on disk. This generalises GeoFuse's RecordingFiles.CheckMapHealth, which does the same
//arithmetic with a hardcoded bytes-per-point per known map name -- here the record size is
//summed from the header's own property declarations.

export const PLY_PROBE_BYTES = 16 * 1024; //headers are well under this

const END_HEADER = 'end_header\n';

const TYPE_BYTES = {
    char: 1, uchar: 1, int8: 1, uint8: 1,
    short: 2, ushort: 2, int16: 2, uint16: 2,
    int: 4, uint: 4, int32: 4, uint32: 4, float: 4, float32: 4,
    double: 8, float64: 8, int64: 8, uint64: 8
};

//chunk: the file's first bytes as text; actualSize: the real size on disk in bytes.
//Returns { text, ok } -- text always carries the header (when there is one) plus the size
//check breakdown, ok drives the tick/cross.
export const buildPlyReport = (chunk, actualSize) => {

    if ('ply' !== chunk.substring(0, 3))
    {
        return { text: 'This file is not a PLY (missing the ply magic line).', ok: false };
    }

    const endIdx = chunk.indexOf(END_HEADER);

    if (endIdx < 0)
    {
        return { text: 'No end_header found in the first ' + PLY_PROBE_BYTES + ' bytes - the PLY header cannot be read.', ok: false };
    }

    const headerLength = endIdx + END_HEADER.length;
    const headerText = chunk.substring(0, headerLength);

    //walk the header collecting elements and summing their per-record bytes
    let binary = false;
    let verifiable = true;
    let unverifiableReason = '';
    const elements = []; //{ name, count, recordBytes }

    for (const rawLine of headerText.split('\n'))
    {
        const line = rawLine.replace(/\r$/, '').trim();
        const parts = line.split(/\s+/);

        if ('format' === parts[0])
        {
            binary = 'binary_little_endian' === parts[1] || 'binary_big_endian' === parts[1];

            if (!binary)
            {
                verifiable = false;
                unverifiableReason = 'ascii PLY data has no fixed record size';
            }
        }
        else if ('element' === parts[0] && 3 === parts.length)
        {
            elements.push({ name: parts[1], count: parseInt(parts[2], 10), recordBytes: 0 });
        }
        else if ('property' === parts[0] && elements.length > 0)
        {
            if ('list' === parts[1])
            {
                verifiable = false;
                unverifiableReason = "element '" + elements[elements.length - 1].name + "' has a variable-length list property";
            }
            else
            {
                const bytes = TYPE_BYTES[parts[1]];

                if (bytes)
                {
                    elements[elements.length - 1].recordBytes += bytes;
                }
                else
                {
                    verifiable = false;
                    unverifiableReason = "unknown property type '" + parts[1] + "'";
                }
            }
        }
    }

    const lines = [headerText];

    lines.push('--- size check ---');
    lines.push('header: ' + headerLength + ' bytes');

    let expected = headerLength;

    for (const element of elements)
    {
        lines.push(element.name + ': ' + element.count + ' x ' + element.recordBytes + ' bytes = ' + (element.count * element.recordBytes) + ' bytes');
        expected += element.count * element.recordBytes;
    }

    let ok = false;

    if (!verifiable)
    {
        lines.push('file size cannot be verified: ' + unverifiableReason);
        lines.push('actual file size: ' + actualSize + ' bytes');
    }
    else
    {
        lines.push('expected file size: ' + expected + ' bytes');
        lines.push('actual file size: ' + actualSize + ' bytes');

        ok = expected === actualSize;
        lines.push(ok ? 'MATCH' : 'MISMATCH (' + (actualSize - expected) + ' bytes difference)');
    }

    return { text: lines.join('\n'), ok: ok };
};

//Fetches just the header bytes (range request) and reads the file's exact size from the
//206 response's Content-Range total. Always resolves to { text, ok } for display.
export const loadPlyInfo = (recording, filePath) => {

    return fetch('maps/' + recording + '/' + filePath, {
        cache: 'no-store',
        headers: { 'Range': 'bytes=0-' + (PLY_PROBE_BYTES - 1) }
    })
    .then((res) => {
        if (!res.ok)
        {
            throw new Error('could not fetch the file (HTTP ' + res.status + ')');
        }

        //Content-Range: bytes 0-16383/123456789 -- the total after the slash is the size
        const contentRange = res.headers.get('content-range') || '';
        const slash = contentRange.lastIndexOf('/');
        const total = slash >= 0 ? parseInt(contentRange.substring(slash + 1), 10) : NaN;

        if (206 !== res.status || isNaN(total))
        {
            throw new Error('server did not answer the range request with the file size');
        }

        return res.text().then(chunk => buildPlyReport(chunk, total));
    })
    .catch(err => ({ text: 'Could not read PLY header: ' + err.message, ok: false }));
};
